(function (global) {
  'use strict';

  const formatter = new Intl.NumberFormat('tr-TR', { maximumFractionDigits: 2 });
  const money = cents => formatter.format(Number(cents) / 100) + ' TL';
  global.CraftlabelNovember = { money };

  // HTTP Date from a non-cached, same-origin Shopify response. Wall-clock
  // changes on the customer's device never move the anchored campaign clock.
  class ServerClock {
    constructor({fetcher=global.fetch?.bind(global), monotonic=()=>global.performance.now()}={}) {
      this.fetcher=fetcher;this.monotonic=monotonic;this.anchor=null;this.syncedAt=null;this.lastAttempt=-Infinity;
    }
    now(){const t=this.monotonic();return this.anchor!==null&&t-this.syncedAt<600000?this.anchor+t-this.syncedAt:null;}
    invalidate(){this.anchor=null;}
    async sync(force=false){
      const before=this.monotonic();
      if(this.pending)return this.pending;
      if(!force&&before-this.lastAttempt<(this.anchor===null?30000:300000))return;
      this.lastAttempt=before;
      this.pending=(async()=>{
        try{
          const nonce=global.crypto.randomUUID();
          const response=await this.fetcher((global.Shopify?.routes?.root||'/')+'cart.js?cl_clock='+nonce,{method:'HEAD',cache:'no-store',credentials:'same-origin',signal:AbortSignal.timeout(8000)});
          const after=this.monotonic(),date=Date.parse(response.headers.get('Date')),age=Number(response.headers.get('Age')||0);
          if(!response.ok||!Number.isFinite(date)||!Number.isFinite(age)||age>0||age<0||after-before>4000)throw Error('Unreliable server time');
          // Date has one-second precision; use the midpoint of that interval.
          this.anchor=date+500+(after-before)/2;this.syncedAt=after;
        }catch(_){/* Keep a recent anchor; a stale one fails closed in now(). */}
        finally{this.pending=null;}
      })();
      return this.pending;
    }
  }
  function countdown(until,now){
    const seconds=Math.max(0,Math.ceil((until-now)/1000)),pad=n=>String(n).padStart(2,'0');
    return {label:seconds<=3600?'Son 1 saat':seconds<=86400?'Son 24 saat':'Bu fırsat için kalan süre',
      text:pad(Math.floor(seconds/86400))+' gün · '+pad(Math.floor(seconds/3600)%24)+' saat · '+pad(Math.floor(seconds/60)%60)+' dk · '+pad(seconds%60)+' sn'};
  }
  function period(start, end) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(start || '') || !/^\d{4}-\d{2}-\d{2}$/.test(end || '')) return null;
    const from = Date.parse(start + 'T00:00:00+03:00');
    const until = Date.parse(end + 'T00:00:00+03:00') + 86400000;
    if (!Number.isFinite(from) || !Number.isFinite(until) || from >= until) return null;
    const valid = value => new Intl.DateTimeFormat('sv-SE', { timeZone: 'Europe/Istanbul' }).format(new Date(Date.parse(value + 'T00:00:00+03:00'))) === value;
    return valid(start) && valid(end) ? { start, end, from, until } : null;
  }

  function stateFor(periods, now = Date.now()) {
    const valid = periods.filter(Boolean).sort((a, b) => a.from - b.from);
    if (!valid.length) return { status: 'unavailable', rank: 3, sort: Infinity };
    const active = valid.find(p => now >= p.from && now < p.until);
    if (active) return { status: 'active', rank: 0, sort: active.until, period: active };
    const next = valid.find(p => now < p.from);
    if (next) return { status: 'upcoming', rank: 1, sort: next.from, period: next };
    return { status: 'ended', rank: 2, sort: -valid[valid.length - 1].until, period: valid[valid.length - 1] };
  }

  function matchesFilter(filter, status, periodStatus, byPeriod=false) {
    return filter==='all'||(byPeriod&&filter==='active'?periodStatus==='active':filter===status);
  }

  function compare(a, b) {
    return a.rank - b.rank || a.sort - b.sort || a.index - b.index;
  }

  if (typeof module === 'object' && module.exports) module.exports = { period, stateFor, compare, money, ServerClock, countdown, matchesFilter };
  if (!global.document || global.customElements.get('cl-november')) return;

  class NovemberCampaigns extends HTMLElement {
    connectedCallback() {
      this.controller?.abort();
      this.controller = new AbortController();
      const { signal } = this.controller;
      this.cards = [...this.querySelectorAll('[data-nov-offer]')];
      this.list = this.querySelector('[data-nov-list]');
      this.empty = this.querySelector('[data-nov-empty]');
      this.status = this.querySelector('[data-nov-count]');
      const schedule=this.querySelector('[data-nov-schedule]');
      if(schedule){try{this.schedule=JSON.parse(schedule.textContent);}catch(_){this.schedule={offers:[]};}}
      this.clock=this.schedule?(global.CraftlabelNovember.clock??=new ServerClock()):null;
      this.motion = global.matchMedia('(prefers-reduced-motion: reduce)');
      this.motion.addEventListener('change', () => {
        if (this.motion.matches) this.getAnimations({ subtree: true }).forEach(animation => animation.cancel());
      }, { signal });
      this.date = new Intl.DateTimeFormat('tr-TR', { day: 'numeric', month: 'long', timeZone: 'Europe/Istanbul' });
      this.filter = this.dataset.defaultFilter || 'all';
      this.querySelectorAll('[data-money-cents]').forEach(node => { node.textContent = money(node.dataset.moneyCents); });
      this.querySelectorAll('[data-nov-price]').forEach(price => {
        const amount = Number(price.dataset.novPrice);
        if (Number.isFinite(amount) && amount >= 0) price.textContent = money(amount * 100);
      });
      this.querySelector('[data-nov-filters]').hidden = false;
      this.querySelectorAll('[data-nov-filter]').forEach(button => button.addEventListener('click', () => {
        this.calendarDay=null;
        this.filter = button.dataset.novFilter;
        this.update();
      }, { signal }));
      this.querySelectorAll('details').forEach(details => details.addEventListener('toggle', () => {
        if (details.open && !this.motion.matches) {
          details.querySelector('[data-nov-detail]')?.animate([{ opacity: 0, transform: 'translateY(-3px)' }, { opacity: 1, transform: 'translateY(0)' }], { duration: 160, easing: 'ease-out' });
        }
      }, { signal }));
      document.addEventListener('shopify:block:select', event => {
        const card = this.cards.find(item => item.contains(event.target) || item === event.target);
        if (card) {
          this.filter = 'all';
          this.update();
          card.scrollIntoView({ block: 'center', behavior: 'instant' });
        }
      }, { signal });
      const resume=()=>{if(!document.hidden){this.clock?.invalidate();this.update();this.clock?.sync(true).then(()=>this.update());}};
      document.addEventListener('visibilitychange',resume,{signal});
      global.addEventListener('pageshow',resume,{signal});
      this.setupCalendar(signal);
      this.update();
      this.clock?.sync(true).then(()=>this.update());
      this.timer = global.setInterval(() => {if(!document.hidden){this.clock?.sync().then(()=>this.update(false));this.update(false);}}, 1000);
    }

    disconnectedCallback() {
      this.controller?.abort();
      global.clearInterval(this.timer);
    }

    onDay(card,day) {
      const at=Date.parse(day+'T12:00:00+03:00');
      return [period(card.dataset.start,card.dataset.end),period(card.dataset.startSecond,card.dataset.endSecond)].some(p=>p&&at>=p.from&&at<p.until);
    }
    setupCalendar(signal) {
      const panel=this.querySelector('[data-calendar]');if(!panel)return;
      const toggle=this.querySelector('[data-calendar-toggle]'),days=panel.querySelector('[data-calendar-days]');
      const choose=day=>{this.selectedCalendarDay=day;days.querySelectorAll('button').forEach(b=>{b.setAttribute('aria-pressed',String(b.dataset.day===day));b.tabIndex=b.dataset.day===day?0:-1;});panel.querySelector('[data-calendar-day-label]').textContent=this.date.format(new Date(day+'T12:00:00+03:00'))+' teklifleri';const offers=panel.querySelector('[data-calendar-offers]');offers.replaceChildren();this.cards.filter(card=>this.onDay(card,day)).forEach(card=>{const b=document.createElement('button');b.type='button';b.disabled=this.hasAttribute('data-purple-vitrine')&&(card.dataset.status!=='active'||card.dataset.campaignReady!=='true');b.textContent=card.querySelector('.cl-nov__title').textContent.trim();b.addEventListener('click',()=>{panel.hidePopover();this.calendarDay=day;this.update();const trigger=card.querySelector('[data-w1-open]');trigger.scrollIntoView({block:'center',behavior:'instant'});trigger.click();},{signal});offers.append(b);});if(!offers.children.length)offers.textContent='Bu gün için kampanya bulunmuyor.';};
      for(let day=1;day<=30;day++){const date='2026-11-'+String(day).padStart(2,'0'),b=document.createElement('button');b.type='button';b.textContent=day;b.dataset.day=date;const count=this.cards.filter(card=>this.onDay(card,date)).length;b.classList.toggle('has-offers',count>0);b.setAttribute('aria-label',day+' Kasım, '+count+' kampanya');b.addEventListener('click',()=>choose(date),{signal});days.append(b);}
      days.firstElementChild.style.gridColumnStart='7';
      days.addEventListener('keydown',event=>{const buttons=[...days.children],i=buttons.indexOf(document.activeElement),step={ArrowLeft:-1,ArrowRight:1,ArrowUp:-7,ArrowDown:7}[event.key];if(i<0||step===undefined)return;event.preventDefault();const b=buttons[Math.max(0,Math.min(29,i+step))];choose(b.dataset.day);b.focus();},{signal});
      toggle.addEventListener('click',()=>{panel.togglePopover();if(panel.matches(':popover-open')){const today=new Intl.DateTimeFormat('sv-SE',{timeZone:'Europe/Istanbul'}).format(new Date());choose(this.selectedCalendarDay||(today.startsWith('2026-11')?today:'2026-11-01'));days.querySelector('[aria-pressed="true"]').focus();}},{signal});
      panel.addEventListener('toggle',()=>toggle.setAttribute('aria-expanded',String(panel.matches(':popover-open'))),{signal});
      panel.querySelector('[data-calendar-close]').addEventListener('click',()=>{panel.hidePopover();toggle.focus();},{signal});
      this.querySelector('[data-calendar-reset]').addEventListener('click',()=>{this.calendarDay=null;this.update();},{signal});
      this.querySelector('[data-show-upcoming]').addEventListener('click',()=>this.querySelector('[data-nov-filter="upcoming"]').click(),{signal});
    }

    periodsFor(card){
      const local=[period(card.dataset.start,card.dataset.end),period(card.dataset.startSecond,card.dataset.endSecond)].filter(Boolean);
      if(!this.schedule)return local;
      const offer=this.schedule.offers.find(o=>o.code===card.dataset.code);
      const matches=offer&&offer.total===Number(card.querySelector('[data-nov-price]').dataset.novPrice)*100&&JSON.stringify(local.map(p=>[p.from,p.until]))===JSON.stringify(offer.periods.map(p=>[Date.parse(p.start),Date.parse(p.end)]));
      card.dataset.scheduleValid=String(!!matches);return matches?local:[];
    }
    update(force=true) {
      const now = this.clock?this.clock.now():Date.now();
      const rows=this.cards.map((card,index)=>{
        const periods=this.periodsFor(card),state=now===null?{status:'unavailable',rank:3,sort:Infinity}:stateFor(periods,now);
        const offer=this.schedule?.offers.find(o=>o.code===card.dataset.code);
        const ready=!!(offer?.mapped&&(offer?.enabled===true||offer?.verified===true)&&card.dataset.scheduleValid==='true'&&card.dataset.selectionMismatch!=='true');
        card.dataset.periodStatus=state.status;card.dataset.campaignReady=String(ready&&now!==null);
        if(this.schedule&&state.status==='active'&&!ready){state.status='unavailable';state.rank=3;}
        const band=card.querySelector('[data-nov-countdown]');
        if(band){band.hidden=!(state.status==='active'&&ready);if(!band.hidden){const c=countdown(state.period.until,now);const label=band.querySelector('[data-countdown-label]'),value=band.querySelector('[data-countdown-value]');if(label.textContent!==c.label)label.textContent=c.label;if(value.textContent!==c.text)value.textContent=c.text;const end=band.querySelector('[data-countdown-end]');const text='Kampanya bitişi: '+new Intl.DateTimeFormat('tr-TR',{timeZone:'Europe/Istanbul',dateStyle:'long',timeStyle:'short'}).format(new Date(state.period.until))+' (İstanbul saati).';if(end.textContent!==text)end.textContent=text;}}
        return {card,index,...state};
      }).sort(compare);
      const key=rows.map(r=>r.card.dataset.code+':'+r.status+':'+r.period?.from+':'+r.card.dataset.campaignReady).join('|')+'|'+this.filter+'|'+this.calendarDay;
      if(!force&&key===this.stateKey)return;this.stateKey=key;
      let visible = 0, statusChanged=false;
      rows.forEach((row, index) => {
        const { card, status, period: current } = row;
        if(card.dataset.status!==status)statusChanged=true;
        card.dataset.status = status;
        if(this.hasAttribute('data-purple-vitrine')){
          const openable=status==='active'&&card.dataset.campaignReady==='true';
          card.querySelectorAll('[data-w1-open], [data-w1-zoom]').forEach(button=>{button.disabled=!openable;});
          const hint=card.querySelector('.cl-purple__choice-hint');
          if(hint){hint.replaceChildren(document.createTextNode(openable?'Renk ve bedenini seç':status==='ended'?'Sona erdi':status==='upcoming'?'Yakında':'Hazırlanıyor'));if(openable){const arrow=document.createElement('span');arrow.setAttribute('aria-hidden','true');arrow.textContent='↗';hint.append(arrow);}}
        }
        const label = card.querySelector('[data-nov-status]');
        const shortDate = (start,end) => {
          const from=new Date(start+'T00:00:00+03:00'), until=new Date(end+'T00:00:00+03:00');
          return start.slice(0,7)===end.slice(0,7) ? Number(start.slice(-2))+'–'+this.date.format(until) : this.date.format(from)+'–'+this.date.format(until);
        };
        const ranges=[period(card.dataset.start,card.dataset.end),period(card.dataset.startSecond,card.dataset.endSecond)].filter(Boolean).map(p=>shortDate(p.start,p.end));
        const suffix=ranges.length?' · '+ranges.join(' / '):'';
        const text = (status==='unavailable'&&this.schedule?(now===null?'Saat doğrulanıyor':card.dataset.code==='W8'?'Kombin hazırlanıyor':'Kampanya hazırlanıyor'):this.dataset[status]) + suffix + (status==='upcoming'&&this.hasAttribute('data-purple-vitrine')?' · '+this.date.format(new Date(current.from))+'’da başlıyor':'');
        if (label.textContent !== text) label.textContent = text;
        const hide = this.calendarDay ? !this.onDay(card,this.calendarDay) : !matchesFilter(this.filter,status,card.dataset.periodStatus,this.hasAttribute('data-period-filter'));
        const focused=this.querySelector('[data-nov-viewport].is-focused');
        if(!focused)card.hidden = hide;
        if (!hide) visible++;
        if (!focused && this.list.children[index] !== card) this.list.insertBefore(card, this.list.children[index] || null);
      });
      this.querySelectorAll('[data-nov-filter]').forEach(button => {
        button.setAttribute('aria-pressed', String(!this.calendarDay && button.dataset.novFilter === this.filter));
        const amount = rows.filter(row => matchesFilter(button.dataset.novFilter,row.status,row.card.dataset.periodStatus,this.hasAttribute('data-period-filter'))).length;
        button.querySelector('[data-nov-filter-count]').textContent = String(amount);
      });
      this.empty.hidden = visible > 0;
      const readiness=this.querySelector('[data-readiness-note]');
      if(readiness)readiness.hidden=!!this.calendarDay||this.filter!=='active'||!rows.some(r=>r.card.dataset.periodStatus==='active'&&r.card.dataset.campaignReady!=='true');
      const message=this.querySelector('[data-empty-message]');
      if(message){const next=rows.filter(r=>r.status==='upcoming').sort((a,b)=>a.sort-b.sort)[0];message.textContent=next?(this.filter==='active'?'Şu anda aktif kampanya yok. ':'Bu görünümde kampanya bulunmuyor. ')+'Sıradaki kampanyalar '+this.date.format(new Date(next.period.from))+'’da başlıyor.':'Bu görünümde kampanya bulunmuyor.';this.querySelector('[data-show-upcoming]').hidden=!next;
        const context=this.querySelector('[data-calendar-context]');context.hidden=!this.calendarDay;if(this.calendarDay)context.querySelector('span').textContent=this.date.format(new Date(this.calendarDay+'T12:00:00+03:00'))+' kampanyaları';
      }
      const count = visible + ' ' + this.dataset.countLabel;
      if (this.status.textContent !== count) this.status.textContent = count;
      if(statusChanged)this.dispatchEvent(new Event('november:status-change'));
    }
  }

  global.customElements.define('cl-november', NovemberCampaigns);
})(typeof window === 'object' ? window : globalThis);
